
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PaymentRequest } from '@/types';
import { useToast } from './use-toast';
import { Database } from '@/integrations/supabase/types';

type PaymentMethodType = Database['public']['Enums']['payment_method_type'];
type PaymentRequestStatus = Database['public']['Enums']['payment_request_status'];

export const usePaymentRequests = (authorId?: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['paymentRequests', authorId],
    queryFn: async (): Promise<PaymentRequest[]> => {
      let query = supabase
        .from('payment_requests')
        .select(`
          *,
          profiles:author_id (
            name,
            email
          )
        `)
        .order('request_date', { ascending: false });
      
      if (authorId) {
        query = query.eq('author_id', authorId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        toast({
          title: 'Error loading payment requests',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data.map(req => ({
        ...req,
        authorId: req.author_id,
        requestDate: req.request_date,
        processedDate: req.processed_date
      })) as unknown as PaymentRequest[];
    }
  });
};

export const useCreatePaymentRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (requestData: {
      authorId: string;
      amount: number;
      paymentMethod: PaymentMethodType;
      notes?: string;
    }) => {
      const dbRequest = {
        author_id: requestData.authorId,
        amount: requestData.amount,
        payment_method: requestData.paymentMethod,
        notes: requestData.notes,
        status: 'pending' as PaymentRequestStatus,
        request_date: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('payment_requests')
        .insert(dbRequest)
        .select()
        .single();
      
      if (error) {
        toast({
          title: 'Error creating payment request',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentRequests'] });
      toast({
        title: 'Success',
        description: 'Payment request has been submitted.',
      });
    }
  });
};

export const useUpdatePaymentRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      notes
    }: {
      id: string;
      status: PaymentRequestStatus;
      notes?: string;
    }) => {
      const updateData: Record<string, any> = { status };
      
      if (status === 'paid') {
        updateData.processed_date = new Date().toISOString();
      }
      
      if (notes !== undefined) {
        updateData.notes = notes;
      }
      
      const { data, error } = await supabase
        .from('payment_requests')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        toast({
          title: 'Error updating payment request',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentRequests'] });
      toast({
        title: 'Success',
        description: 'Payment request has been updated.',
      });
    }
  });
};
